import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaBan,
  FaBullhorn,
  FaCheck,
  FaCheckDouble,
  FaCircle,
  FaClock,
  FaCode,
  FaEye,
  FaImage,
  FaPen,
  FaPlay,
  FaRedo,
  FaSearch,
  FaShareAlt,
  FaTimes,
  FaVideo,
} from "react-icons/fa";
import InfoTip from "../../components/InfoTip";
import { fmtShortDate, initials, relativeDay, statusLabel, statusTone } from "./workApi";
import "../../assets/css/PageHeading.css";
import "./work.css";

/** Standard Cloud Office page heading (title with the accent underline, optional Info pill, actions on the right). */
export function PageHead({ title, info, children }) {
  return (
    <div className="co-page-head wk-head">
      <div className="wk-head__title">
        <h2 className="co-page-head__title">{title}</h2>
        {info ? <InfoTip label={`About ${title}`}>{info}</InfoTip> : null}
      </div>
      {children ? <div className="dmp-actions wk-head__actions">{children}</div> : null}
    </div>
  );
}

const STATUS_ICONS = {
  "Not Started": FaCircle,
  "In Progress": FaPlay,
  "In Review": FaEye,
  "Changes Needed": FaRedo,
  Approved: FaCheck,
  Scheduled: FaClock,
  Posted: FaCheckDouble,
  Done: FaCheckDouble,
  Blocked: FaBan,
};

const AREA_ICONS = {
  video: FaVideo,
  image: FaImage,
  pen: FaPen,
  share: FaShareAlt,
  bullhorn: FaBullhorn,
  search: FaSearch,
  code: FaCode,
};

/** Status with colour, icon and words (never colour alone). */
export function StatusPill({ status, small = false }) {
  const Icon = STATUS_ICONS[status] || FaCircle;
  return (
    <span className={`wk-pill wk-pill--${statusTone(status)}${small ? " wk-pill--sm" : ""}`}>
      <Icon aria-hidden="true" className="wk-pill__icon" />
      {statusLabel(status)}
    </span>
  );
}

export function AreaChip({ name, color, icon }) {
  if (!name) return null;
  const Icon = AREA_ICONS[icon] || FaCircle;
  return (
    <span className="wk-area" style={{ "--wk-area": color || "#64748b" }}>
      <Icon aria-hidden="true" />
      {name}
    </span>
  );
}

export function Avatar({ name, size = 28 }) {
  return (
    <span className="wk-avatar" style={{ width: size, height: size, fontSize: size * 0.4 }} title={name || "Unassigned"}>
      {name ? initials(name) : "?"}
    </span>
  );
}

export function DueText({ date, today, overdue, label = "Due" }) {
  if (!date) return null;
  return (
    <span className={`wk-due${overdue ? " is-late" : ""}`}>
      {label} {fmtShortDate(date)}
      <span className="wk-due__rel"> · {relativeDay(date, today)}</span>
    </span>
  );
}

/** People select grouped into "Your team" and "Client team". */
export function PersonSelect({ people, value, onChange, blank = "Pick a person", id, required, disabled }) {
  const own = (people || []).filter((p) => p.Team !== "client");
  const client = (people || []).filter((p) => p.Team === "client");
  const option = (p) => (
    <option key={p.User_Id} value={p.User_Id}>
      {p.Name}
      {p.Primary_Area ? ` · ${p.Primary_Area}` : ""}
    </option>
  );
  return (
    <select
      id={id}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      required={required}
      disabled={disabled}
    >
      <option value="">{blank}</option>
      {client.length ? <optgroup label="Your team">{own.map(option)}</optgroup> : own.map(option)}
      {client.length ? <optgroup label="Client team">{client.map(option)}</optgroup> : null}
    </select>
  );
}

function useEscape(onClose) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
}

function useInitialFocus(ref) {
  useEffect(() => {
    const prev = document.activeElement;
    const el = ref.current?.querySelector("input, select, textarea, button:not(.wk-close)");
    (el || ref.current)?.focus?.();
    return () => prev?.focus?.();
  }, [ref]);
}

export function Modal({ title, onClose, children, footer, wide = false }) {
  const ref = useRef(null);
  useEscape(onClose);
  useInitialFocus(ref);
  return (
    <div className="wk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        className={`wk-modal${wide ? " wk-modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        tabIndex={-1}
      >
        <div className="wk-modal__head">
          <h2 className="wk-modal__title">{title}</h2>
          <button type="button" className="wk-close" onClick={onClose} aria-label="Close">
            <FaTimes aria-hidden="true" />
          </button>
        </div>
        <div className="wk-modal__body">{children}</div>
        {footer ? <div className="wk-modal__foot">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Drawer({ title, onClose, children }) {
  const ref = useRef(null);
  useEscape(onClose);
  useInitialFocus(ref);
  return (
    <div className="wk-overlay wk-overlay--drawer" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <aside className="wk-drawer" role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
        <div className="wk-modal__head">
          <h2 className="wk-modal__title">{title}</h2>
          <button type="button" className="wk-close" onClick={onClose} aria-label="Close">
            <FaTimes aria-hidden="true" />
          </button>
        </div>
        <div className="wk-drawer__body">{children}</div>
      </aside>
    </div>
  );
}

/**
 * Accessible replacement for window.prompt:
 *   const [promptUi, ask] = usePrompt();
 *   const reason = await ask({ title: "Reject", label: "Why?", required: true });  // null when cancelled
 *   return <>{promptUi}...</>;
 */
export function usePrompt() {
  const [state, setState] = useState(null);
  const resolveRef = useRef(null);

  const ask = useCallback(
    (opts) =>
      new Promise((resolve) => {
        resolveRef.current?.(null);
        resolveRef.current = resolve;
        setState({ value: opts.initial || "", ...opts });
      }),
    []
  );

  const finish = (value) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setState(null);
  };

  const ui = state ? (
    <Modal
      title={state.title || "Enter a value"}
      onClose={() => finish(null)}
      footer={
        <>
          <button type="button" className="dmp-btn dmp-btn--ghost wk-btn-lg" onClick={() => finish(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="dmp-btn wk-btn-lg"
            disabled={state.required && !state.value.trim()}
            onClick={() => finish(state.value.trim())}
          >
            {state.confirmLabel || "OK"}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!state.required || state.value.trim()) finish(state.value.trim());
        }}
      >
        <label className="wk-field">
          {state.label}
          {state.multiline ? (
            <textarea value={state.value} onChange={(e) => setState((s) => ({ ...s, value: e.target.value }))} placeholder={state.placeholder} />
          ) : (
            <input
              type={state.type || "text"}
              value={state.value}
              onChange={(e) => setState((s) => ({ ...s, value: e.target.value }))}
              placeholder={state.placeholder}
            />
          )}
          {state.hint ? <span className="wk-field__hint">{state.hint}</span> : null}
        </label>
      </form>
    </Modal>
  ) : null;

  return [ui, ask];
}

export function Empty({ icon: Icon, title, children }) {
  return (
    <div className="wk-empty">
      {Icon ? <Icon aria-hidden="true" className="wk-empty__icon" /> : null}
      <div className="wk-empty__title">{title}</div>
      {children ? <div className="wk-empty__text">{children}</div> : null}
    </div>
  );
}

/** Compact, clickable deliverable card used on My Work, Plan lanes and Team Board. */
export function ItemCard({ item, today, onOpen, showProject = true, actions }) {
  const target = item.Planned_Post_Date && !item.Due_Date ? item.Planned_Post_Date : item.Target_Date;
  const label = item.Planned_Post_Date && !item.Due_Date ? "Post" : "Due";
  return (
    <div className={`wk-card${item.Is_Overdue ? " is-late" : ""}`}>
      <button type="button" className="wk-card__main" onClick={() => onOpen?.(item)} aria-label={`Open ${item.Title}`}>
        <div className="wk-card__top">
          <span className="wk-card__title">{item.Title}</span>
          <StatusPill status={item.Status} small />
        </div>
        <div className="wk-card__meta">
          {showProject && item.Project_Name ? <span className="wk-card__project">{item.Project_Name}</span> : null}
          {showProject && item.Tenant_Name ? <span className="wk-muted">· {item.Tenant_Name}</span> : null}
          <AreaChip name={item.Area_Name} color={item.Area_Color} icon={item.Area_Icon} />
          <span className="wk-muted">{item.Item_Type}</span>
        </div>
        <div className="wk-card__foot">
          <DueText date={target} today={today} overdue={item.Is_Overdue} label={label} />
          {item.Planned_Post_Date && item.Due_Date ? (
            <span className="wk-muted">Post {fmtShortDate(item.Planned_Post_Date)}</span>
          ) : null}
          <span className="wk-card__who">
            <Avatar name={item.Executor_Name} size={22} />
            <span>{item.Executor_Name || "Unassigned"}</span>
          </span>
        </div>
        {item.Progress_Pct ? (
          <div className="wk-progress" aria-label={`${item.Progress_Pct}% done`}>
            <span style={{ width: `${item.Progress_Pct}%` }} />
          </div>
        ) : null}
      </button>
      {actions ? <div className="wk-card__actions">{actions}</div> : null}
    </div>
  );
}
